/// Boat anatomy — the vocabulary every Competent Crew / Power L1
/// student needs by name. Split into four views, each with its own
/// 100 × 100 SVG-coordinate space:
///
///   - **yacht profile** — hull, deck, cockpit, cabin, rudder, key
///     deck fittings seen from the side.
///   - **rigging** — standing rigging (mast, boom, stays, spreaders)
///     and running rigging (halyards, sheets, kicker, outhaul).
///   - **powerboat profile** — planing hull, console, outboard /
///     sterndrive, trim tabs, kill cord.
///   - **below decks** — engine bay + sea-cocks + fuel + water +
///     batteries + bilge as a cutaway diagram.
///
/// Each part carries its own (x, y) inside the view's coordinate
/// space so the figure component can plot a labelled dot at the right
/// spot. Identifying the view a part belongs to is the discriminator.

export type BoatView =
  | "yacht-profile"
  | "rigging"
  | "powerboat"
  | "below-decks";

export type RiggingKind = "standing" | "running";

export interface BoatPart {
  /// Stable identifier — used by the recognition game.
  id: string;
  /// Display name — used in the reveal and as the multi-choice
  /// answer.
  name: string;
  /// One-line plain-English meaning shown in the reveal.
  meaning: string;
  /// Operational "why" — the use the student remembers it for.
  use: string;
  /// Which figure this part appears on.
  view: BoatView;
  /// Position on that view's 100 × 100 coordinate space (the figure
  /// component scales into the SVG viewbox).
  x: number;
  y: number;
  /// For rigging only — standing (structural) vs running (sail-control).
  riggingKind?: RiggingKind;
}

/// ───────────────────────────────────────────────────────────────────
/// Yacht profile — side view, mast forward of cabin, cockpit aft,
/// rudder at stern. Coordinate origin is the chart's top-left.
/// ───────────────────────────────────────────────────────────────────

const YACHT: readonly BoatPart[] = [
  {
    id: "bow",
    name: "Bow",
    meaning: "The forward end of the boat.",
    use: "Direction of travel; where the anchor lives; pulpit + bow roller mounted here.",
    view: "yacht-profile",
    x: 12,
    y: 64,
  },
  {
    id: "stern",
    name: "Stern",
    meaning: "The aft end of the boat.",
    use: "Where the rudder and (on most yachts) the outboard or transom-mounted engine live.",
    view: "yacht-profile",
    x: 90,
    y: 66,
  },
  {
    id: "hull",
    name: "Hull",
    meaning: "The waterproof shell of the boat below the deck.",
    use: "Provides buoyancy + form stability. Hull shape (long-keel vs fin-keel vs bilge-keel) drives sailing characteristics.",
    view: "yacht-profile",
    x: 50,
    y: 78,
  },
  {
    id: "deck",
    name: "Deck",
    meaning: "The horizontal walking surface on top of the hull.",
    use: "Movement around the boat; clip onto a jackstay before going forward in heavy weather.",
    view: "yacht-profile",
    x: 38,
    y: 56,
  },
  {
    id: "coachroof",
    name: "Coachroof",
    meaning: "The raised cabin top — separates the saloon below from the deck above.",
    use: "Houses the deck hatches, mast collar, sometimes solar panels and traveller.",
    view: "yacht-profile",
    x: 52,
    y: 48,
  },
  {
    id: "cockpit",
    name: "Cockpit",
    meaning: "The sunken aft area where the helmsman sits and the sails are trimmed.",
    use: "Working space underway; main + jib sheets lead here; chartplotter + instruments mounted on the helm pod.",
    view: "yacht-profile",
    x: 72,
    y: 56,
  },
  {
    id: "companionway",
    name: "Companionway",
    meaning: "The opening + steps down from the cockpit into the cabin.",
    use: "The boundary between weather and shelter. Wash-boards in stormy weather; sea-rail at the foot of the steps.",
    view: "yacht-profile",
    x: 62,
    y: 54,
  },
  {
    id: "saloon",
    name: "Saloon",
    meaning: "The main cabin — settees + table + galley access.",
    use: "Crew rest area; chart table is usually here.",
    view: "yacht-profile",
    x: 50,
    y: 70,
  },
  {
    id: "heads",
    name: "Heads",
    meaning: "The toilet compartment.",
    use: "Sea-water flush — close the inlet sea-cock before leaving the boat.",
    view: "yacht-profile",
    x: 36,
    y: 70,
  },
  {
    id: "galley",
    name: "Galley",
    meaning: "The boat's kitchen — stove, sink, fridge.",
    use: "Cooking on gimbals so it stays level when the boat heels. Gas isolator at the appliance + at the bottle.",
    view: "yacht-profile",
    x: 58,
    y: 70,
  },
  {
    id: "focsle",
    name: "Fo'c's'le",
    meaning: "Forecabin — the cabin at the bow, usually a V-berth.",
    use: "Sleeping space in port; in a seaway it's the worst-pitching part of the boat.",
    view: "yacht-profile",
    x: 22,
    y: 70,
  },
  {
    id: "bilge",
    name: "Bilge",
    meaning: "The lowest internal space, below the cabin sole.",
    use: "Where water collects; bilge pump empties it. Check daily — fresh water in the bilge means a leak.",
    view: "yacht-profile",
    x: 48,
    y: 86,
  },
  {
    id: "rudder",
    name: "Rudder",
    meaning: "The pivoting blade under the stern that steers the boat.",
    use: "Worked by the tiller (smaller boats) or wheel via cables / hydraulics (larger boats).",
    view: "yacht-profile",
    x: 92,
    y: 86,
  },
  {
    id: "pulpit",
    name: "Pulpit",
    meaning: "The stainless guard rail around the bow.",
    use: "Stops you falling off the foredeck; anchor + nav-light mounting points.",
    view: "yacht-profile",
    x: 8,
    y: 48,
  },
  {
    id: "pushpit",
    name: "Pushpit",
    meaning: "The stainless guard rail around the stern.",
    use: "Mounts horseshoe lifebuoy, dan-buoy, stern light, sometimes the wind generator.",
    view: "yacht-profile",
    x: 96,
    y: 52,
  },
  {
    id: "stanchions",
    name: "Stanchions",
    meaning: "Vertical posts around the deck edge, carrying the guard wires.",
    use: "Anchor the lifelines (top + bottom). DO NOT use as a step or grab-handle — they can pivot in their sockets.",
    view: "yacht-profile",
    x: 28,
    y: 50,
  },
  {
    id: "jackstays",
    name: "Jackstays",
    meaning: "Webbing straps run fore-and-aft along the deck for clipping tethers onto.",
    use: "Clip on at the companionway BEFORE stepping out; stay clipped on for the full journey forward.",
    view: "yacht-profile",
    x: 44,
    y: 52,
  },
];

/// ───────────────────────────────────────────────────────────────────
/// Rigging — standing and running. Drawn on a focused mast / boom
/// figure so labels don't fight the hull.
/// ───────────────────────────────────────────────────────────────────

const RIGGING: readonly BoatPart[] = [
  {
    id: "mast",
    name: "Mast",
    meaning: "Vertical spar carrying the sails.",
    use: "Held up by the standing rigging; sail tracks or bolt-rope groove receives the luff.",
    view: "rigging",
    x: 20,
    y: 50,
    riggingKind: "standing",
  },
  {
    id: "boom",
    name: "Boom",
    meaning: "Horizontal spar along the foot of the main.",
    use: "Carries the foot; mainsheet + kicker + outhaul all bear on it.",
    view: "rigging",
    x: 50,
    y: 80,
    riggingKind: "standing",
  },
  {
    id: "gooseneck",
    name: "Gooseneck",
    meaning: "The hinged joint where the boom meets the mast.",
    use: "Lets the boom swing horizontally + lift vertically. Reef hooks live just below it.",
    view: "rigging",
    x: 22,
    y: 80,
    riggingKind: "standing",
  },
  {
    id: "forestay",
    name: "Forestay",
    meaning: "Stay from the masthead to the bow — holds the mast against the wind.",
    use: "Carries the jib's luff; tensioned by the backstay (forestay can't be slack).",
    view: "rigging",
    x: 6,
    y: 50,
    riggingKind: "standing",
  },
  {
    id: "backstay",
    name: "Backstay",
    meaning: "Stay from the masthead to the stern — opposes the forestay.",
    use: "Sets forestay tension (more backstay → flatter jib). Some yachts have a runner-and-checkstay arrangement.",
    view: "rigging",
    x: 38,
    y: 50,
    riggingKind: "standing",
  },
  {
    id: "cap-shroud",
    name: "Cap shrouds",
    meaning: "Wires from the masthead down to the chainplates at deck level.",
    use: "Stop the mast falling sideways. Tensioned tight on the leeward side when heeled.",
    view: "rigging",
    x: 28,
    y: 32,
    riggingKind: "standing",
  },
  {
    id: "lower-shroud",
    name: "Lower shrouds",
    meaning: "Wires from the spreader root (or lower) down to the chainplates.",
    use: "Stop the lower part of the mast bending sideways at the spreader.",
    view: "rigging",
    x: 24,
    y: 58,
    riggingKind: "standing",
  },
  {
    id: "spreader",
    name: "Spreader",
    meaning: "Horizontal strut on the mast pushing the cap shrouds outboard.",
    use: "Improves the shroud's angle so it pulls the mast TIGHTER against compression. Don't grab a spreader — chafe-strip protects the leech of the main from it.",
    view: "rigging",
    x: 36,
    y: 38,
    riggingKind: "standing",
  },
  {
    id: "chainplate",
    name: "Chainplate",
    meaning: "Stainless plate bolted through the deck (and hull) — anchors the shrouds.",
    use: "Highest-load fitting on the boat. Inspect annually for cracks + leaking sealant.",
    view: "rigging",
    x: 50,
    y: 64,
    riggingKind: "standing",
  },
  {
    id: "main-halyard",
    name: "Main halyard",
    meaning: "Line that hoists the mainsail.",
    use: "Runs from the head, up over a masthead sheave, down inside (or outside) the mast to a winch + clutch on the cabin top.",
    view: "rigging",
    x: 12,
    y: 22,
    riggingKind: "running",
  },
  {
    id: "jib-halyard",
    name: "Jib halyard",
    meaning: "Line that hoists the jib (or genoa).",
    use: "Runs up the front of the mast inside the foil of a furling system, or to a sheave at the masthead.",
    view: "rigging",
    x: 4,
    y: 22,
    riggingKind: "running",
  },
  {
    id: "main-sheet",
    name: "Mainsheet",
    meaning: "Line that controls the boom's position across the boat.",
    use: "Trims the main: sheet in upwind, ease out downwind. Runs through a tackle to multiply the force.",
    view: "rigging",
    x: 56,
    y: 92,
    riggingKind: "running",
  },
  {
    id: "kicker",
    name: "Kicker / vang",
    meaning: "Tackle from the boom down to the mast base.",
    use: "Stops the boom rising when the mainsheet is eased off-wind. Critical control off the wind — eased mainsheet without kicker = useless leech.",
    view: "rigging",
    x: 32,
    y: 86,
    riggingKind: "running",
  },
  {
    id: "outhaul",
    name: "Outhaul",
    meaning: "Line that pulls the clew aft along the boom.",
    use: "Tighten upwind for a flat sail; ease downwind for a fuller, more powerful shape.",
    view: "rigging",
    x: 78,
    y: 80,
    riggingKind: "running",
  },
  {
    id: "cunningham",
    name: "Cunningham",
    meaning: "Line that tensions the luff of the main below the head.",
    use: "Move draft forward (in stronger wind) by pulling the luff DOWN at an eye above the tack.",
    view: "rigging",
    x: 22,
    y: 70,
    riggingKind: "running",
  },
  {
    id: "topping-lift",
    name: "Topping lift",
    meaning: "Line from the masthead to the boom-end, supporting the boom when the sail is dropped.",
    use: "Stops the boom landing on your head when the main comes down. Slack it off once the sail is hoisted.",
    view: "rigging",
    x: 78,
    y: 56,
    riggingKind: "running",
  },
  {
    id: "traveller",
    name: "Traveller",
    meaning: "Slider on a transverse track, anchoring the mainsheet base.",
    use: "Sheet position across the boat. Pull traveller to windward → main goes higher up + more closed-leech (upwind); centre traveller for general use.",
    view: "rigging",
    x: 50,
    y: 92,
    riggingKind: "running",
  },
];

/// ───────────────────────────────────────────────────────────────────
/// Powerboat profile — planing-hull RIB / sportsboat side view.
/// ───────────────────────────────────────────────────────────────────

const POWERBOAT: readonly BoatPart[] = [
  {
    id: "p-bow",
    name: "Bow",
    meaning: "The pointed forward end of the boat.",
    use: "Where the bow eye + cleat + nav light are mounted. Planing hulls usually rise sharply at the bow (the 'V').",
    view: "powerboat",
    x: 10,
    y: 60,
  },
  {
    id: "p-transom",
    name: "Transom",
    meaning: "The flat aft end of the hull.",
    use: "Where an outboard or sterndrive is mounted; bilge drain plug lives here.",
    view: "powerboat",
    x: 90,
    y: 64,
  },
  {
    id: "p-hull",
    name: "Planing hull",
    meaning: "A hull shape (deep-V or moderate-V) designed to rise out of the water at speed.",
    use: "Below planing speed the hull is in displacement mode (slow, fuel-hungry); above planing speed it rides on the surface (efficient, much faster).",
    view: "powerboat",
    x: 50,
    y: 80,
  },
  {
    id: "deadrise",
    name: "Deadrise",
    meaning: "The angle of the V at the transom — how deep the hull cuts into the water.",
    use: "Deep-V (~24°) is comfortable in chop but slow to plane; flat (~5°) planes early but pounds. RIB tenders are usually 20–24°.",
    view: "powerboat",
    x: 80,
    y: 86,
  },
  {
    id: "console",
    name: "Console",
    meaning: "The standing helm position with wheel, throttle and instruments.",
    use: "Where the skipper drives from. Open-cockpit boats have just the console; cabin RIBs have a small cuddy in front.",
    view: "powerboat",
    x: 56,
    y: 38,
  },
  {
    id: "wheel-throttle",
    name: "Wheel + throttle/shift",
    meaning: "Steering wheel + the throttle lever that's also the gear shift.",
    use: "Throttle lever centred = neutral; push forward = ahead; pull back = astern. Past the detent in either direction engages gear.",
    view: "powerboat",
    x: 60,
    y: 42,
  },
  {
    id: "kill-cord",
    name: "Kill cord",
    meaning: "A lanyard from the engine cut-out clip to the helmsman's wrist or buoyancy aid.",
    use: "If the helmsman is thrown from the helm, the cord pulls the clip out and the engine stops. ALWAYS attached, every time the engine is running.",
    view: "powerboat",
    x: 64,
    y: 50,
  },
  {
    id: "outboard",
    name: "Outboard / sterndrive",
    meaning: "The engine drive unit at the transom — outboard (all-in-one outside the hull) or sterndrive (engine inside, leg outside).",
    use: "Tilt up out of the water on a trailer or on a beach. Outboards swing for steering; sterndrives have a fixed shaft + steering through the leg.",
    view: "powerboat",
    x: 94,
    y: 70,
  },
  {
    id: "trim-tab",
    name: "Trim tab",
    meaning: "Small flap on the transom (or sterndrive leg) that controls the boat's running attitude.",
    use: "Adjust to keep the bow down at speed (avoiding porpoising) or up at slow speed (avoiding stuffing the bow into a swell).",
    view: "powerboat",
    x: 88,
    y: 80,
  },
  {
    id: "p-cleat",
    name: "Bow cleat",
    meaning: "T-shaped fitting at the bow for tying mooring lines.",
    use: "Two cleats forward (port + starboard) and two aft. Bowline goes here on tow.",
    view: "powerboat",
    x: 8,
    y: 46,
  },
  {
    id: "bow-eye",
    name: "Bow eye",
    meaning: "Stainless ring at the very front of the bow, at the waterline.",
    use: "Trailering winch attaches here; emergency towing point.",
    view: "powerboat",
    x: 4,
    y: 64,
  },
  {
    id: "p-nav-lights",
    name: "Nav lights",
    meaning: "Red (port) + green (starboard) bow sidelights, white stern light, all-round white masthead light.",
    use: "Switched on between sunset and sunrise + restricted visibility. Sidelights usually at the bow; stern light at the transom.",
    view: "powerboat",
    x: 22,
    y: 34,
  },
];

/// ───────────────────────────────────────────────────────────────────
/// Below decks — cutaway showing engine bay + fuel + water + power
/// systems + sea-cocks.
/// ───────────────────────────────────────────────────────────────────

const BELOW_DECKS: readonly BoatPart[] = [
  {
    id: "engine",
    name: "Engine",
    meaning: "The marine diesel (or petrol) propelling unit.",
    use: "Heart of the boat. Daily WOBBLE checks before starting.",
    view: "below-decks",
    x: 50,
    y: 50,
  },
  {
    id: "fuel-tank",
    name: "Fuel tank",
    meaning: "Stainless or polythene tank holding the boat's diesel / petrol.",
    use: "Fill from a deck filler with a breather to vent fumes. Plumbed via a fuel line + primary filter to the engine.",
    view: "below-decks",
    x: 20,
    y: 70,
  },
  {
    id: "fuel-filter",
    name: "Fuel filter / water separator (Racor)",
    meaning: "Primary filter on the fuel line, with a glass bowl that traps water.",
    use: "Check daily — water in the bowl means contaminated fuel. Bleed clean before the secondary filter.",
    view: "below-decks",
    x: 35,
    y: 60,
  },
  {
    id: "raw-water-cock",
    name: "Raw-water sea-cock",
    meaning: "Through-hull valve admitting sea water for engine cooling.",
    use: "Open it before starting; close it on lay-up. Strainer just downstream catches weed + plastic.",
    view: "below-decks",
    x: 70,
    y: 78,
  },
  {
    id: "raw-strainer",
    name: "Raw-water strainer",
    meaning: "Glass-bowl filter on the raw-water inlet, just downstream of the sea-cock.",
    use: "Inspect daily — a basket full of weed strangles cooling flow and the engine overheats.",
    view: "below-decks",
    x: 64,
    y: 70,
  },
  {
    id: "bilge-pump",
    name: "Bilge pump",
    meaning: "Manual + electric pumps that lift water out of the bilge to a through-hull above the waterline.",
    use: "Electric on an auto-float switch; manual handle accessible from the cockpit. Test both at the daily check.",
    view: "below-decks",
    x: 48,
    y: 78,
  },
  {
    id: "battery-house",
    name: "House battery",
    meaning: "Deep-cycle battery (or bank) powering the boat's lights, instruments, fridge, autopilot.",
    use: "Discharged + recharged hundreds of times. Inspect terminals annually; replace at ~50% capacity loss.",
    view: "below-decks",
    x: 78,
    y: 36,
  },
  {
    id: "battery-start",
    name: "Engine-start battery",
    meaning: "Cranking battery dedicated to engine start — kept isolated so house drain never strands you.",
    use: "Wired through an isolator switch (1 / 2 / Both / Off). For starting only — don't run the fridge off it.",
    view: "below-decks",
    x: 78,
    y: 50,
  },
  {
    id: "isolator",
    name: "Battery isolator",
    meaning: "Selector switch routing power from house / start / both banks to the boat's circuits.",
    use: "Switch off when leaving the boat for more than a day. NEVER turn through 'Off' with the engine running — kills the alternator diodes.",
    view: "below-decks",
    x: 86,
    y: 28,
  },
  {
    id: "water-tank",
    name: "Fresh-water tank",
    meaning: "Polythene or stainless tank holding the boat's drinking + washing water.",
    use: "Fill at a marina; sterilise once a season. Plumbed to galley + heads through a pressurised pump.",
    view: "below-decks",
    x: 22,
    y: 30,
  },
  {
    id: "holding-tank",
    name: "Holding tank",
    meaning: "Tank that collects black-water (sewage) from the heads.",
    use: "Required in many ports + marinas; pumped out ashore. Vent line essential to avoid pressure build-up.",
    view: "below-decks",
    x: 38,
    y: 30,
  },
  {
    id: "gas-locker",
    name: "Gas locker",
    meaning: "Sealed locker housing the LPG bottle, vented overboard at its base.",
    use: "Vent must be ABOVE the waterline + drain to the outside (gas is heavier than air — pools in the bilge if it leaks).",
    view: "below-decks",
    x: 12,
    y: 12,
  },
  {
    id: "stern-gland",
    name: "Stern gland / packing",
    meaning: "Watertight gland where the propeller shaft exits the hull.",
    use: "Greaser-fed; a steady drip is normal under-way, a stream is not. Repack every few seasons.",
    view: "below-decks",
    x: 86,
    y: 76,
  },
];

/// The full catalogue, joined and frozen.
export const BOAT_PARTS: readonly BoatPart[] = Object.freeze([
  ...YACHT,
  ...RIGGING,
  ...POWERBOAT,
  ...BELOW_DECKS,
]);

export const BOAT_PART_IDS: readonly string[] = BOAT_PARTS.map((p) => p.id);

/// Filter to a single view — used by the figure component and the
/// game's per-view mode.
export function partsForView(v: BoatView): readonly BoatPart[] {
  return BOAT_PARTS.filter((p) => p.view === v);
}
